'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { createLoreNote, askQwen } from '@/lib/actions';
import { ArrowLeft, Sparkles, Save, Wand2 } from 'lucide-react';
import Link from 'next/link';

export default function NewLorePage() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [brainDump, setBrainDump] = useState('');
  const [isParsing, setIsParsing] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    category: '',
    content: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBrainDump = async () => {
    if (!brainDump.trim()) return;
    setIsParsing(true);
    try {
      const prompt = `Parse this messy brain dump into a JSON object for a story lore note. The brain dump: "${brainDump}". Return ONLY JSON with: title, category, content.`;
      const response = await askQwen(prompt, "You extract lore details into JSON.");
      const jsonStr = response.match(/\{[\s\S]*\}/)?.[0];
      if (jsonStr) {
        setFormData(JSON.parse(jsonStr));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsParsing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const data = new FormData();
    Object.entries(formData).forEach(([k, v]) => data.append(k, v));
    data.append('custom_fields', '[]');
    try {
      const id = await createLoreNote(data);
      router.push(`/lore/${id}`);
      router.refresh();
    } catch (err) {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col p-8">
      <Link href="/lore" className="flex items-center gap-2 text-slate-400 mb-8"><ArrowLeft size={20} /> Back to Library</Link>
      <div className="max-w-4xl mx-auto w-full grid grid-cols-1 md:grid-cols-2 gap-12">
        <div className="space-y-6">
           <h2 className="text-3xl font-serif font-bold text-emerald-400 italic flex items-center gap-2"><Sparkles /> The Oracle's Whisper</h2>
           <textarea value={brainDump} onChange={(e) => setBrainDump(e.target.value)} placeholder="Brain dump your lore... 'There's a myth about a giant snake that sleeps under Zrada...'" className="w-full h-48 bg-slate-900 border border-slate-800 rounded-2xl p-4 text-sm" />
           <button onClick={handleBrainDump} disabled={isParsing} className="w-full bg-emerald-600 py-3 rounded-xl font-bold flex items-center justify-center gap-2">
             {isParsing ? 'Oracle is writing...' : <><Wand2 size={18} /> Record Lore</>}
           </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6 bg-slate-900/50 p-8 rounded-3xl border border-slate-800">
           <input required name="title" value={formData.title} onChange={handleChange} placeholder="Lore Topic / Title" className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3" />
           <input name="category" value={formData.category} onChange={handleChange} placeholder="Category (e.g. Mythology, History, Faction)" className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3" />
           <textarea name="content" value={formData.content} onChange={handleChange} placeholder="The Legend..." rows={8} className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4" />
           <button type="submit" disabled={loading} className="w-full bg-emerald-600 py-4 rounded-xl font-bold flex items-center justify-center gap-2"><Save /> {loading ? 'Saving...' : 'Record Lore Note'}</button>
        </form>
      </div>
    </div>
  );
}
